// Single source of truth for rental duration + timezone math. Server-authoritative
// (the widget must NOT import this — keep it out of the widget bundle). Uses built-in
// Intl for DST-correct conversions; no third-party dependency.

const DAY_MS = 86_400_000;

function zonedParts(utcMs: number, tz: string) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(new Date(utcMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)!.value);
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour'), mi: get('minute'), s: get('second') };
}

/**
 * Convert a local wall-clock (date + hour) in an IANA tz to the true UTC instant.
 * Uses two correction passes to handle DST transitions correctly: near a gap/fold,
 * the first-pass corrected instant may land in a different offset region, so a second
 * pass re-samples the offset at the corrected instant and converges to the right result.
 */
export function localTimeToUtc(dateISO: string, hour: number, tz: string): Date {
  const [y, mo, d] = dateISO.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(y, mo - 1, d, hour, 0, 0);
  // Pass 1: compute offset at the naive-UTC guess, apply first correction
  const p1 = zonedParts(guess, tz);
  const asUtc1 = Date.UTC(p1.y, p1.mo - 1, p1.d, p1.h, p1.mi, p1.s);
  const offset1 = asUtc1 - guess;
  const t1 = guess - offset1;
  // Pass 2: re-sample the offset at the corrected instant to handle DST transitions
  const p2 = zonedParts(t1, tz);
  const asUtc2 = Date.UTC(p2.y, p2.mo - 1, p2.d, p2.h, p2.mi, p2.s);
  const offset2 = asUtc2 - t1;
  return new Date(guess - offset2);
}

/** Authoritative billing rule: ceil of elapsed 24h periods, minimum 1. */
export function billableDays(pickupAt: Date, returnAt: Date): number {
  const ms = returnAt.getTime() - pickupAt.getTime();
  return Math.max(1, Math.ceil(ms / DAY_MS));
}

/** Legacy inclusive +1 day-count — ONLY for rows with no captured times. */
export function legacyInclusiveDays(dateFrom: string, dateTo: string): number {
  const a = new Date(`${dateFrom}T00:00:00Z`).getTime();
  const b = new Date(`${dateTo}T00:00:00Z`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 1;
  return Math.max(1, Math.round((b - a) / DAY_MS) + 1);
}

/** Use instants when present, else fall back to the legacy inclusive count. */
export function resolveBillableDays(b: {
  pickupAt?: Date | string | null;
  returnAt?: Date | string | null;
  dateFrom: string;
  dateTo: string;
}): number {
  if (b.pickupAt && b.returnAt) {
    return billableDays(new Date(b.pickupAt), new Date(b.returnAt));
  }
  return legacyInclusiveDays(b.dateFrom, b.dateTo);
}
